/**
 * Environment variable validation
 *
 * This module validates that all required environment variables are present
 * at startup and provides type-safe access to them.
 */

interface RequiredEnvVars {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
}

class EnvironmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentError';
  }
}

/**
 * Validates that a required environment variable is defined and non-empty
 */
function validateEnvVar(name: keyof RequiredEnvVars, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new EnvironmentError(
      `Missing required environment variable: ${name}\n\n` +
      `Please ensure your .env file contains:\n` +
      `${name}=your_value_here\n\n` +
      `If you're running locally, copy .env.example to .env and fill in the values.\n` +
      `If you're deploying, ensure environment variables are configured in your deployment platform.`
    );
  }
  return value;
}

/**
 * Validates all required environment variables
 * Call this early in your application startup (e.g., in main.tsx)
 */
export function validateEnvironment(): void {
  console.log('[ENV] Validating environment variables...');

  try {
    validateEnvVar('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL);
    validateEnvVar('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY);

    console.log('[ENV] ✓ All required environment variables are present');
  } catch (error) {
    if (error instanceof EnvironmentError) {
      console.error('[ENV] ✗ Environment validation failed:');
      console.error(error.message);

      // Show a user-friendly error in the browser
      document.body.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 2rem;
          background: linear-gradient(to bottom right, #1e293b, #0f172a);
          font-family: system-ui, -apple-system, sans-serif;
        ">
          <div style="
            max-width: 600px;
            padding: 2rem;
            background: white;
            border-radius: 8px;
            box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
          ">
            <h1 style="
              margin: 0 0 1rem 0;
              font-size: 1.5rem;
              font-weight: 700;
              color: #dc2626;
            ">
              ⚠️ Configuration Error
            </h1>
            <p style="margin: 0 0 1rem 0; color: #475569; line-height: 1.6;">
              The application is missing required environment variables and cannot start.
            </p>
            <pre style="
              margin: 0;
              padding: 1rem;
              background: #f1f5f9;
              border-radius: 4px;
              overflow-x: auto;
              font-size: 0.875rem;
              color: #1e293b;
            ">${error.message.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
          </div>
        </div>
      `;

      // Prevent the app from rendering
      throw error;
    }
    throw error;
  }
}

/**
 * Type-safe access to validated environment variables
 * Only use this after validateEnvironment() has been called
 */
export const env = {
  get SUPABASE_URL(): string {
    return import.meta.env.VITE_SUPABASE_URL;
  },
  get SUPABASE_ANON_KEY(): string {
    return import.meta.env.VITE_SUPABASE_ANON_KEY;
  },
} as const;
